import { pipe } from 'fp-ts/function';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { ActionType, ArticlePath, FeatureFlag, createGuideLink, TextKey } from '@mews-commander/enums';
import { CommanderThunkDispatch } from '@mews-commander/types';
import { ErrorHandler, ModalBodyWrapper, ModalWrapper, OverlayedLoader } from '@mews-commander/ui';
import { IconName } from '@optimus-web/core';
import { TimeUnit } from '@mews/enums';
import { compactArray } from '@mews/fp';
import { LocalizedText, lt, showSuccessAlert, useFeatureFlag, useLocalizeText } from '@mews/framework';
import { getTimeUnitByServiceId } from '@mews-commander/legacy-entities/src/ducks';
import { useCommanderSelector } from '@mews-commander/utils';
import { HeadingAction } from '../../../../legacy/React/Components/HeadingAction/Action/types';
import ModalHeader from '../../../../legacy/React/Components/Common/ModalHeader';
import { useCloseButton } from '../../../../core/windowManager/hooks';
import { addRestrictionAndSwitchToEditMode, deleteRestrictionHeadingAction, updateRestriction } from '../../actions';
import { ServiceIdContext, VisitHasIntegrationRestrictionsContext } from '../contexts';
import { RESTRICTIONS_CALENDAR_QUERY_KEY, useRestrictions } from '../../queries';
import { FormType, FormValues, RestrictionFormViewProps } from './types';
import { MultistepPreviewForm } from './MultistepPreviewForm';
import { RestrictionFormContainer } from './initialize';

const getArticlePath = ({
    formType,
    timeUnit,
    isRestrictionSplicingEnabled,
}: {
    formType: FormType;
    timeUnit: TimeUnit;
    isRestrictionSplicingEnabled: boolean;
}) => {
    if (!isRestrictionSplicingEnabled || timeUnit !== TimeUnit.Day) {
        return ArticlePath.ManagingRestrictions;
    }
    switch (formType) {
        case 'createForm':
            return ArticlePath.HowToCreateRestrictions;
        case 'editForm':
            return ArticlePath.HowToEditAndDeleteRestrictions;
        case 'deleteForm':
            return ArticlePath.HowToEditAndDeleteRestrictions;
    }
};

const RestrictionFormView = ({ serviceId, ...other }: RestrictionFormViewProps) => {
    const dispatch: CommanderThunkDispatch = useDispatch();
    const isRestrictionSplicingEnabled = useFeatureFlag(FeatureFlag.restrictionSplicingEnabled);
    const result = useRestrictions({
        serviceId,
        formType: other.formType,
        restrictionId: other.formType === 'editForm' ? other.restrictionId : null,
    });
    const localizeText = useLocalizeText();

    const timeUnit = useCommanderSelector((state) => getTimeUnitByServiceId(state, { serviceId }));

    const closeButton = useCloseButton();
    const queryClient = useQueryClient();
    const actions: HeadingAction[] = compactArray([
        other.formType === 'editForm'
            ? {
                  icon: IconName.Delete,
                  actionId: 'delete',
                  text: <LocalizedText textKey={TextKey.DeleteHint} />,
                  title: (
                      <LocalizedText
                          textKey={TextKey.DeleteConfirmation}
                          substitutes={{ entity: localizeText({ textKey: TextKey.Restriction }) }}
                      />
                  ),
                  buttonText: <LocalizedText textKey={TextKey.Delete} />,
                  isDanger: true,
                  onConfirmation: async () => {
                      await dispatch(deleteRestrictionHeadingAction({ serviceId, id: other.restrictionId }));
                      await queryClient.invalidateQueries({ queryKey: RESTRICTIONS_CALENDAR_QUERY_KEY });
                  },
                  type: ActionType.ConfirmationDialog,
              }
            : null,
        {
            url: pipe(
                { formType: other.formType, timeUnit, isRestrictionSplicingEnabled },
                getArticlePath,
                createGuideLink
            ),
            target: '_blank',
            icon: IconName.Help,
            text: <LocalizedText textKey={TextKey.Help} />,
            type: ActionType.Link,
        },
    ]);

    const getFormTitle = () => {
        if (isRestrictionSplicingEnabled && timeUnit === TimeUnit.Day && other.formType !== 'editForm') {
            return other.formType === 'createForm' ? TextKey.CreateNewRestrictions : TextKey.DeleteRestrictions;
        }
        return other.formType === 'editForm' ? TextKey.EditRestriction : TextKey.CreateNewRestriction;
    };

    const handleFormSubmit = async (formValues: FormValues) => {
        switch (formValues.formType) {
            case 'createForm':
                await dispatch(addRestrictionAndSwitchToEditMode(formValues, serviceId));
                break;
            case 'editForm': {
                await dispatch(updateRestriction({ formValues, serviceId }));
                showSuccessAlert({ description: lt('RestrictionHasBeenEdited') });
                break;
            }
            default:
        }
        await queryClient.invalidateQueries({ queryKey: RESTRICTIONS_CALENDAR_QUERY_KEY });
    };

    return (
        <ServiceIdContext.Provider value={serviceId}>
            <ModalWrapper>
                <ModalHeader
                    title={<LocalizedText textKey={getFormTitle()} />}
                    actions={actions}
                    primaryAction={closeButton}
                />
                <ModalBodyWrapper padded>
                    {result.status === 'pending' && <OverlayedLoader show />}
                    {result.status === 'error' && <ErrorHandler />}
                    {result.status === 'success' && (
                        <VisitHasIntegrationRestrictionsContext.Provider
                            value={result.data.visitHasIntegrationRestrictions}
                        >
                            {isRestrictionSplicingEnabled && timeUnit === TimeUnit.Day ? (
                                // @ts-expect-error: multiConditions, boolean is not assignable to type false
                                <MultistepPreviewForm
                                    {...other}
                                    multiConditions={other.formType !== 'editForm'}
                                    serviceId={serviceId}
                                />
                            ) : (
                                // Needs to have an additional wrapper to only allow 'createForm' and 'editForm' form types
                                // @ts-expect-error: multiConditions, boolean is not assignable to type false
                                <RestrictionFormContainer
                                    {...other}
                                    multiConditions={false}
                                    multistep={false}
                                    serviceId={serviceId}
                                    handleSubmit={handleFormSubmit}
                                />
                            )}
                        </VisitHasIntegrationRestrictionsContext.Provider>
                    )}
                </ModalBodyWrapper>
            </ModalWrapper>
        </ServiceIdContext.Provider>
    );
};

export default RestrictionFormView;
